'use client'

import * as React from 'react'
import { Button } from '@repo/ui/button'
import { XIcon } from '@phosphor-icons/react'
import { COOKIE_CONSENT_KEY } from '@/shared/lib/constants'

type CookieConsent = {
  necessary: boolean
  analytics: boolean
  marketing: boolean
  timestamp: string
}

export function CookieBanner() {
  const [showBanner, setShowBanner] = React.useState(false)
  const [showSettings, setShowSettings] = React.useState(false)
  const [consent, setConsent] = React.useState<Partial<CookieConsent>>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  React.useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!stored) {
      setShowBanner(true)
    }
  }, [])

  const saveConsent = (consentData: Partial<CookieConsent>) => {
    const fullConsent: CookieConsent = {
      necessary: true,
      analytics: consentData.analytics ?? false,
      marketing: consentData.marketing ?? false,
      timestamp: new Date().toISOString(),
    }
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(fullConsent))
    setShowBanner(false)
  }

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true })
  }

  const acceptNecessary = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false })
  }

  const savePreferences = () => {
    saveConsent(consent)
  }

  if (!showBanner) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
      <div className="max-w-4xl mx-auto bg-[#141817] border border-[#2e3833] rounded-lg shadow-xl">
        {!showSettings ? (
          <div className="p-4 md:p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">
                  We value your privacy
                </h3>
                <p className="text-slate-300 text-sm mb-4">
                  We use cookies to enhance your browsing experience, serve
                  personalized content, and analyze our traffic. By clicking
                  "Accept All", you consent to our use of cookies. Read our{' '}
                  <a href="/privacy" className="text-[#80cca5] hover:underline">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href="/cookies" className="text-[#80cca5] hover:underline">
                    Cookie Policy
                  </a>
                  .
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary-cta" size="sm" onClick={acceptAll}>
                    Accept All
                  </Button>
                  <Button
                    variant="secondary-cta"
                    size="sm"
                    onClick={acceptNecessary}
                  >
                    Necessary Only
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSettings(true)}
                    className="text-slate-300 hover:text-white"
                  >
                    Cookie Settings
                  </Button>
                </div>
              </div>
              <button
                onClick={acceptNecessary}
                className="text-slate-400 hover:text-white p-1"
                aria-label="Close"
              >
                <XIcon className="size-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                Cookie Settings
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-slate-400 hover:text-white p-1"
                aria-label="Close"
              >
                <XIcon className="size-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <CookieOption
                title="Necessary Cookies"
                description="Required for the website to function. Cannot be disabled."
                checked={true}
                disabled={true}
              />
              <CookieOption
                title="Analytics Cookies"
                description="Help us understand how visitors interact with our website."
                checked={consent.analytics ?? false}
                onChange={(checked) =>
                  setConsent((prev) => ({ ...prev, analytics: checked }))
                }
              />
              <CookieOption
                title="Marketing Cookies"
                description="Used to deliver personalized advertisements."
                checked={consent.marketing ?? false}
                onChange={(checked) =>
                  setConsent((prev) => ({ ...prev, marketing: checked }))
                }
              />
            </div>

            <div className="flex gap-3">
              <Button variant="primary-cta" size="sm" onClick={savePreferences}>
                Save Preferences
              </Button>
              <Button variant="secondary-cta" size="sm" onClick={acceptAll}>
                Accept All
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function CookieOption({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string
  description: string
  checked: boolean
  disabled?: boolean
  onChange?: (checked: boolean) => void
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-1 size-4 rounded border-slate-600 bg-slate-800 text-[#80cca5] focus:ring-[#80cca5] focus:ring-offset-slate-900 disabled:opacity-50"
      />
      <div>
        <div className="text-white font-medium text-sm">{title}</div>
        <div className="text-slate-400 text-sm">{description}</div>
      </div>
    </label>
  )
}
